import { applyApplicationPatch, ApplicationPatchOp } from '@numeo/types-main-web-app'
import {
    ClientApplication,
    ClientUser,
    Company,
    ExtensionSignUpData,
    FeatureKey,
    ForgotPasswordData,
    GoogleProfile,
    GoogleSignInProfile,
    SignInData,
    UserNylasEmails,
    UserRole,
} from '@numeo/types-core'
import { createAsyncThunk, createSlice, current, PayloadAction, Slice } from '@reduxjs/toolkit'
import axios, { AxiosError } from 'axios'
import { isEqual } from 'lodash'
import { sanitizeApplication, sanitizeUser } from 'hooks/auth/utils/user-sanitization'
import { LS_EMAIL_KEY, LS_PHONE_KEY } from 'pages/dashboard/pages/settings/components/Tabs/DevelopersSettings'
import { setErrorTrackingUser } from 'services/errorTracking'
import { ExtensionApplication, extensionAuthService, ExtensionUser } from 'services/ExtensionAuthService'
import { updaterLiteAuthService } from 'services/UpdaterLiteAuthService'
import {
    AUTH_TOKEN,
    clearAuthToken,
    clearFailedTokenAndCheckBaseSurvives,
    clearSignupGates,
    getAuthToken,
    hasSessionOverride,
    logoutAuthToken,
    setAuthToken,
    switchAuthToken,
} from 'setup/SetupAxios'
import { clearPrivateTransferStorage } from 'setup/privateTransferStorage'
import { RootState } from './index'
import { patchDriverStatusDelta, patchDriverStatusReducer, patchDriverStatus } from './driverStatusMutation'
export interface DebugData {
    spotFinder: {
        mockBrokerPhoneNumber?: string
        mockBrokerEmail?: string
    }
}

/**
 * Multi-MC Phase 2 (multi_mc/ADR-003): admin-side type shim mirroring the parent-branch
 * `ClientOrganization` shape returned by `/auth/verify` and `/v1/organizations/me`. The wire shape is
 * FLAT: organization fields sit on the root, with the optional Phase 2 `applications[]` mirror present
 * only when `companyFromParentEnabled === true` (see ADR-003 §"Compatibility contract per consumer"
 * and the FLAT shape sample under §"`/auth/verify` Response (Phase 2 mode)"). `isOwner` rides at the
 * same level — it is computed by the server and consumed by owner-only UI gates here in admin.
 *
 * Drop the explicit `companyFromParentEnabled?`/`isOwner` fields and inline `emails` once
 * `@numeo/types` exports the parent-branch shape end-to-end.
 */
interface OrganizationApplicationEmail {
    email: string
    isPrimary: boolean
}

interface OrganizationApplicationSummary {
    id: string
    projectName: string
    company: Company | { name?: string; mcNumber?: string; dotNumber?: string } | null
    status: 'active' | 'archived' | string
    isActive?: boolean
    emails?: OrganizationApplicationEmail[]
    /**
     * Per-company rollout gate for the Multi-MC per-tab session isolation (PR #9795), independent
     * of `multiMcEnabled` below (which only controls whether the org can have multiple companies
     * at all). Backed by @numeo/types' FeatureType.PerTabCompanySwitch — toggle via the Gluon
     * feature-flag editor, not a bespoke endpoint. Always present alongside this summary — see
     * organization.service.ts's `base` object (derived via isPerTabCompanySwitchEnabled()).
     * SWITCH callers must check the TARGET application's flag (not the currently active one) and
     * fall back to `setAuthToken` (old global switch) when it's false.
     */
    perTabCompanySwitchEnabled: boolean
}

export interface OrganizationData {
    id: string
    name?: string
    ownerId: string
    applicationIds: string[]
    multiMcEnabled: boolean
    companyFromParentEnabled?: boolean
    /**
     * Per-app summaries with `isActive`/`emails`. Present only when
     * `companyFromParentEnabled === true`. Older Phase 1 orgs and non-org users will not see this
     * field at all (per ADR-003 contract — old clients must not branch on its existence).
     */
    applications?: OrganizationApplicationSummary[]
    isOwner: boolean
    /**
     * Org-scoped company-add cap (equals the plan's email-connection entitlement). `null` means
     * unlimited (e.g. ULTRA). Used by CompanySwitcher to gate the "Add Company" button — the
     * active app's subscription is not carried on the auth payload, so the limit must come from here.
     */
    companiesLimit?: number | null
}

export interface AuthState {
    user: ClientUser | null
    quotaUsage: Record<FeatureKey, number> | null
    application: ClientApplication | null
    organizationData: OrganizationData | null
    featureFlags: Record<string, boolean>
    debugData: DebugData
    isAuthenticated: boolean
    isLoading: boolean
    verifyFailed: boolean // Prevents auto-redirect loop when extension sets stale data
}

const initialState: AuthState = {
    user: null,
    quotaUsage: null,
    application: null,
    organizationData: null,
    featureFlags: {},
    debugData: {
        spotFinder: {
            mockBrokerPhoneNumber: '',
            mockBrokerEmail: '',
        },
    },
    isAuthenticated: false,
    isLoading: true,
    verifyFailed: false,
}

// Reactive auth→extension sync (EXTENSION_DATA / NUMEO_AUTH_SUCCESS / numeo-auth-changed) lives in
// store/middleware/extensionSyncMiddleware.ts, NOT in these reducers. Keeping reducers pure lets the
// middleware run the sync once per dispatch (off the React render path) with a content diff, instead
// of every one of the ~308 useAuth consumers re-serializing the full payload on each socket tick.
const authSlice: Slice<AuthState> = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUser: (state, action: PayloadAction<ClientUser | null>) => {
            state.user = action.payload
            state.isAuthenticated = Boolean(action.payload)

            setErrorTrackingUser(
                action.payload
                    ? {
                          id: action.payload.id,
                          email: action.payload.email,
                          companyName: state.application?.projectName,
                      }
                    : null
            )
            // Extension sync (numeo-auth-changed + postMessage) is emitted by extensionSyncMiddleware.
        },
        setQuotaUsage: (state, action: PayloadAction<Record<FeatureKey, number> | null>) => {
            state.quotaUsage = action.payload
        },
        setApplication: (state, action: PayloadAction<ClientApplication | null>) => {
            let incoming = action.payload

            // Preserve the billing-authoritative subscription. Only GET /auth/verify computes the derived
            // subscription overlay (`subscription.featureFlags`, incl. featureFlags.TMS) from the canonical
            // org SubscriptionRepo (ADR-020). Every OTHER source — the ~30 REST mutation endpoints that echo
            // `{ application }` (updater settings, roles, integrations, accounting…) and the socket
            // APP_ApplicationUpdated push — serializes the RAW embedded application.subscription with NO
            // overlay. Letting one replace the good subscription flips hasTmsAccess to false and shows paying
            // TMS customers a false "Your trial has ended" screen until the next verifyAuth (page refresh).
            // These payloads are never the billing channel, so keep the subscription already hydrated from
            // /auth/verify. Detected by the overlay's presence: verify always attaches `featureFlags`, the raw
            // embedded subscription never does. (Central chokepoint — supersedes the per-handler guard #9870
            // added for the socket vector, which only covered one of the many callers.)
            if (incoming && state.application?.subscription) {
                const incomingSub = incoming.subscription
                if (!incomingSub || !('featureFlags' in incomingSub)) {
                    incoming = { ...incoming, subscription: state.application.subscription }
                }
            }

            // Skip no-op updates. The socket's APP_ApplicationUpdated re-sends a fresh-but-equal
            // object on every tick (sanitizeApplication clones), and assigning a new reference here
            // would needlessly re-render all ~308 useAuth consumers and re-trigger the extension sync.
            if (isEqual(state.application, incoming)) return
            state.application = incoming

            if (state.user) {
                setErrorTrackingUser({
                    id: state.user.id,
                    email: state.user.email,
                    companyName: incoming?.projectName,
                })
            }
            // Extension sync is emitted by extensionSyncMiddleware on the auth/setApplication action.
        },
        // The server's application push: only the fields that changed, applied to the application already loaded.
        patchApplication: (state, action: PayloadAction<ApplicationPatchOp[]>) => {
            if (!state.application) return
            state.application = applyApplicationPatch(current(state.application) as ClientApplication, action.payload)
        },
        setOrganizationData: (state, action: PayloadAction<OrganizationData | null>) => {
            state.organizationData = action.payload
        },
        setFeatureFlags: (state, action: PayloadAction<Record<string, boolean>>) => {
            state.featureFlags = action.payload
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        setVerifyFailed: (state, action: PayloadAction<boolean>) => {
            state.verifyFailed = action.payload
        },
        updateDebugData: (state, action: PayloadAction<DebugData>) => {
            state.debugData = action.payload
        },
        // Optimistic update for seat count after addSeats API succeeds.
        // The Stripe webhook will eventually sync the full entitlements, but
        // this prevents a stale UI between the API response and webhook arrival.
        updateSubscriptionSeats: (state, action: PayloadAction<number>) => {
            if (state.application?.subscription) {
                state.application.subscription.seats = action.payload
                if (state.application.subscription.entitlements?.teamMember) {
                    state.application.subscription.entitlements.teamMember.hard_limit = action.payload
                }
            }
        },
        logout: () => {
            setErrorTrackingUser(null)
            clearPrivateTransferStorage()
            // logoutAuthToken(), not a raw removeItem: an explicit logout must end THIS tab's
            // session outright (never silently fall back to the base company the way an automatic
            // 401/verify-failure invalidation does — see clearFailedTokenAndCheckBaseSurvives), but
            // if this tab is only running under its own company-switch override, the shared base
            // token every OTHER open tab depends on via getAuthToken()'s fallback must survive.
            logoutAuthToken()
            localStorage.removeItem('qr_session_id')
            localStorage.removeItem('ai_filter_loads_v1')
            localStorage.removeItem('ai_filter_rows')
            // numeo:tmsAuthUser is deliberately NOT removed (not part of clearSignupGates): it marks
            // this browser as a TMS surface so the sidebar's TMS "Home" entry survives logout →
            // re-login through any login page (TMS accounts without Fleet/Accounting/Safety
            // features have no server-side marker yet, so this flag is their only signal).
            clearSignupGates()

            // True once no BASE session remains ANYWHERE in this browser after logoutAuthToken()
            // above — not "was THIS tab the base session." That distinction matters when tabs are
            // logged out one at a time: if the base tab logs out first, a sibling tab still running
            // under its own override sees hasSessionOverride() === true for ITSELF regardless, but
            // the shared base is already gone by the time IT logs out — and that sibling tab's own
            // window/content-script still needs to be told, or the extension stays stuck showing a
            // stale signed-in state for that tab. Checking localStorage directly, after the fact,
            // gives the right answer regardless of which tab acts first or how many are open.
            const baseSessionGone = !localStorage.getItem(AUTH_TOKEN)

            // loadops_auth_data and the extension sync below are browser-wide, not per-tab (same
            // reasoning as extensionSyncMiddleware.ts / verifyAuth's guard elsewhere in this file):
            // the extension stays pinned to the shared BASE session regardless of any tab's
            // company-switch override. Telling the extension "signed out" (or wiping
            // loadops_auth_data) while a base session is STILL valid elsewhere would incorrectly
            // propagate a real sign-out to every other open tab — the "logs out then auto-syncs
            // back in" loop this guard prevents. Only run these once the base is genuinely gone.
            if (baseSessionGone) {
                localStorage.removeItem('loadops_auth_data')
                extensionAuthService.syncAuthState(null).catch((error) => {
                    console.error('Failed to sync logout with extension:', error)
                })
            }

            // Dispatch auth sync event for extension content script. Also gated on baseSessionGone:
            // the extension's content script (numeoContentScript.ts) listens for this event and
            // re-reads this page's accessToken/loadops_auth_data in response. While a base session
            // is still valid elsewhere, that data is still the untouched, still-valid base session —
            // so firing this event here risks the extension re-reading and resyncing to that base
            // identity right back into this tab immediately after it navigates to the sign-in page.
            // NOTE: as of this writing the extension does not reliably clear its OWN stored auth
            // state in response to this event even when baseSessionGone is true and detail carries
            // no token (see project memory: extension Multi-MC support is deferred separately) —
            // this gate gets the ADMIN side's signal correct, but fully fixing the round trip
            // requires a corresponding extension-side change that hasn't been made yet.
            // Use source tag so the admin's own event listener (useAuthRedux) ignores self-triggered events
            if (baseSessionGone && typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('numeo-auth-changed', { detail: { source: 'admin-app' } }))
            }
            return { ...initialState, isLoading: false }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(patchDriverStatusDelta, patchDriverStatusReducer)
        builder.addCase(patchDriverStatus, patchDriverStatusReducer)
    },
})

// Action creators
export const {
    setUser,
    setQuotaUsage,
    setApplication,
    patchApplication,
    setOrganizationData,
    setFeatureFlags,
    setLoading,
    setVerifyFailed,
    updateDebugData,
    updateSubscriptionSeats,
    logout,
} = authSlice.actions

// Utility function to create standardized extension auth data
const createExtensionAuthData = (
    token: string,
    user: ClientUser | null,
    application: ClientApplication | null = null,
    quotaUsage?: Record<string, number | null> // Quota usage from API response
): ExtensionUser | null => {
    if (!user) return null

    return {
        token,
        userId: user.id,
        email: user.email,
        name: user.name,
        emailTemplates: user.emailTemplates || { bodyTemplates: [], footer: '' },
        nylasEmails: user.nylasEmails ? mapNylasEmailsToExtensionFormat(user.nylasEmails) : [],
        integrations: user.integrations || {},
        application: application
            ? ({
                  projectName: application.projectName,
                  features: application.features || [],
                  integrations: application.integrations || {},
                  settings: application.settings || {},
                  subscription: application.subscription, // Include subscription at application level
                  quotaUsage: quotaUsage, // Include quota usage (remaining counts for each feature)
              } as ExtensionApplication)
            : undefined,
    }
}

// Builds a minimal auth payload for localStorage to avoid quota errors.
// Only stores what the extension content script and LoginV2 actually need.
// The full application data is passed via postMessage and re-fetched on verifyAuth.
const buildMinimalAuthData = (token: string, responseData: { user?: Record<string, unknown>; application?: Record<string, unknown> }) => {
    const { user, application } = responseData
    return {
        token,
        user: user
            ? {
                  _id: user._id,
                  email: user.email,
                  name: user.name,
                  emailTemplates: user.emailTemplates,
                  nylasEmails: user.nylasEmails,
              }
            : undefined,
        application: application
            ? {
                  projectName: application.projectName,
                  features: application.features,
                  settings: application.settings,
                  subscription: application.subscription,
                  integrations: application.integrations
                      ? {
                            nylas: (application.integrations as Record<string, unknown>).nylas,
                        }
                      : undefined,
              }
            : undefined,
    }
}

// Utility function to sync auth state with extension with error handling
const syncWithExtension = async (authData: ExtensionUser | null, operation: string) => {
    const extensionConnected = localStorage.getItem('numeo-extension-connected') === 'true'
    if (!extensionConnected || typeof window === 'undefined') {
        return
    }

    try {
        if (authData) {
            // Send auth data via window.postMessage
            window.postMessage(
                {
                    type: 'NUMEO_AUTH_SUCCESS',
                    user: authData,
                    source: 'numeo-web',
                },
                '*'
            )
        } else {
            // For logout, send a logout message
            window.postMessage(
                {
                    type: 'NUMEO_AUTH_LOGOUT',
                    source: 'numeo-web',
                },
                '*'
            )
        }
    } catch (error) {
        // Silently ignore postMessage errors (non-cloneable objects)
        console.warn(`[authSlice] Could not sync ${operation} with extension (non-cloneable data)`, error)
    }
}

// Thunks
export const verifyAuth = createAsyncThunk('auth/verifyAuth', async (_, { dispatch, rejectWithValue }) => {
    const token = getAuthToken()

    if (!token) {
        // Sync logout with extension — but only if there's truly no base session anywhere in this
        // browser. getAuthToken() also resolves null for a tab that just ended its own
        // company-switch override (logoutAuthToken's LOGGED_OUT_FLAG) while the shared base token
        // in localStorage is still perfectly valid for sibling tabs; telling the extension "signed
        // out" in that case would incorrectly propagate a sign-out to every other open tab — the
        // same class of bug already guarded in authSlice's logout reducer and useAuthRedux's
        // handleAuthChanged. Only sync null when the base session itself is genuinely gone too.
        if (!localStorage.getItem(AUTH_TOKEN)) {
            await syncWithExtension(null, 'logout during token verification')
        }

        dispatch(setUser(null))
        dispatch(setLoading(false))
        dispatch(setQuotaUsage(null))
        return null
    }

    try {
        const response = await axios.get('/auth/verify')

        // identify moved to useAuthRedux centralized useEffect

        // Sanitize user data
        const sanitizedUser = sanitizeUser(response.data.user)
        if (sanitizedUser) {
            dispatch(setUser(sanitizedUser))
        }

        // Sanitize application data
        let sanitizedApplication: ClientApplication | null = null
        if (response.data.application) {
            sanitizedApplication = sanitizeApplication(response.data.application, sanitizedUser)
            dispatch(setApplication(sanitizedApplication))
        }

        dispatch(setQuotaUsage(response.data.quotaUsage || null))
        dispatch(setOrganizationData(response.data.organization || null))
        const flags = response.data.featureFlags ?? {}
        dispatch(setFeatureFlags(flags))

        // Sync auth verification with extension — but only when `token` is the shared base session.
        // A switched tab reloads after every switch (see CompanySwitcher et al.), which runs
        // verifyAuth with the override token; pushing that to the extension via postMessage is just
        // as browser-wide as the loadops_auth_data write below, so it needs the same guard — the
        // extension would otherwise flip-flop between companies as differently-switched tabs reload.
        if (!hasSessionOverride()) {
            const extensionAuthData = createExtensionAuthData(token, sanitizedUser, sanitizedApplication, response.data.quotaUsage)
            await syncWithExtension(extensionAuthData, 'auth verification')

            // Save loadops_auth_data so the extension gets fresh data after verifyAuth.
            localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
        }

        dispatch(setLoading(false))
        return response.data
    } catch (error) {
        console.error('Auth verification error:', error)
        // clearFailedTokenAndCheckBaseSurvives, not a hasSessionOverride()-before/after check: the
        // global axios response interceptor (SetupAxios.ts) reacts to this exact same 401 and runs
        // BEFORE this catch block sees the rejection (interceptors are part of the promise chain
        // ahead of the code that awaited axios.get), so it may have already cleared the override by
        // the time we get here — a plain "was there an override" read would then always see "no
        // override" and wipe every other tab's data anyway. This helper decides by comparing the
        // failed token's identity against each storage slot's CURRENT contents (authTokenStorage.ts),
        // which gives the correct answer regardless of who clears first, and is a safe no-op on a
        // second call for an already-cleared token.
        const baseSurvives = clearFailedTokenAndCheckBaseSurvives(error)
        // loadops_auth_data/signup-gates are keyed to the shared BASE identity and read
        // browser-wide (extension, OnboardingAuthGuard, useAuthCheck, clientApiExtension). Only wipe
        // them when the base session is actually gone too — i.e. this wasn't a per-tab override
        // failure. Otherwise a switched tab's override expiring (or the switched company's access
        // being revoked) would spuriously wipe every OTHER open tab's still-valid base session data —
        // the mirror of the leak the extension-sync guard above already prevents on the success path.
        if (!baseSurvives) {
            localStorage.removeItem('loadops_auth_data')
            // The session died without a logout, so clear the signup-only gates too — otherwise a
            // DIFFERENT account logging in on this browser would inherit the pending flag and get the
            // mandatory MC modal, writing company info to the wrong app. numeo:tmsAuthUser intentionally
            // survives (see logout) so the TMS sidebar persists.
            clearSignupGates()
        }
        dispatch(setUser(null))
        dispatch(setQuotaUsage(null))
        dispatch(setVerifyFailed(true))
        dispatch(setLoading(false))
        return rejectWithValue(error)
    }
})

export const login = createAsyncThunk('auth/login', async (formData: SignInData, { dispatch, rejectWithValue }) => {
    try {
        const response = await axios.post('/auth/sign-in', formData)
        const { token, user, application, quotaUsage } = response.data

        // Use sanitizeUser function to properly sanitize user data
        const sanitizedUser = sanitizeUser(user)
        // Use sanitizeApplication function to properly sanitize application data
        const sanitizedApplication = sanitizeApplication(application, sanitizedUser)

        // Sync login with extension using utility function
        const extensionAuthData = createExtensionAuthData(token, sanitizedUser, sanitizedApplication)
        await syncWithExtension(extensionAuthData, 'login')

        setAuthToken(token)
        localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
        dispatch(setQuotaUsage(quotaUsage))
        dispatch(setVerifyFailed(false))
        dispatch(setUser(sanitizedUser))
        dispatch(setApplication(sanitizedApplication))
        return response.data
    } catch (error) {
        // Ensure the error is propagated to the component's catch block
        return rejectWithValue(error)
    }
})

/**
 * Admin impersonation. The gluon operator (operator-authorized on the backend) mints a session token
 * for the target account and hands it to us via the sign-in URL. We store it and hydrate through the
 * normal /auth/verify path — no customer credential is ever involved, so this works for accounts with
 * no login method of their own. Returns the verified session data, or rejects if the token is invalid.
 */
export const adminLoginAsToken = createAsyncThunk('auth/adminLoginAsToken', async (token: string, { dispatch, rejectWithValue }) => {
    try {
        // switchAuthToken, not a raw localStorage write: impersonating a user is a per-tab action,
        // not a fresh login for the whole browser — every other open tab the impersonating operator
        // has (doing their own, unrelated work) must not suddenly start acting as the impersonated
        // user. verifyAuth() below already gates its extension-sync and loadops_auth_data writes on
        // hasSessionOverride(), so writing the override here first is what makes this impersonation
        // session correctly per-tab-isolated end to end — same reasoning as the email/password
        // adminLoginAs this replaces, and CompanySwitcher before that.
        switchAuthToken(token)
        clearSignupGates()
        // verifyAuth reads the active token via getAuthToken() (session override first), calls
        // /auth/verify, and populates the store (user/application/brokers/quota) — the same path as
        // a page refresh.
        const data = await dispatch(verifyAuth()).unwrap()
        if (!data) {
            throw new Error('Impersonation token was rejected')
        }
        dispatch(setVerifyFailed(false))
        return data
    } catch (error) {
        // Identity-scoped, not a blind localStorage removal: only clears this token if it's still
        // sitting in the slot switchAuthToken wrote it to, so a second/racing call can't clobber a
        // different session.
        clearAuthToken(token)
        console.error('[authSlice] Admin impersonation failed:', error instanceof Error ? error.message : 'Unknown error')

        let errorMessage = 'Admin login failed'
        if (axios.isAxiosError(error)) {
            errorMessage = error.response?.data?.message || error.message || errorMessage
        } else if (error instanceof Error) {
            errorMessage = error.message
        }
        return rejectWithValue({ message: errorMessage })
    }
})

export const extensionSignup = createAsyncThunk('auth/extensionSignup', async (formData: ExtensionSignUpData, { dispatch, rejectWithValue }) => {
    try {
        const response = await axios.post('/auth/sign-up/ext/verify', { ...formData, source: 'admin_web' })
        const { token, user, application } = response.data

        const sanitizedUser = sanitizeUser(user)

        // Use sanitizeApplication function to properly sanitize application data
        const sanitizedApplication = sanitizeApplication(application, sanitizedUser)

        extensionAuthService
            .syncAuthState({
                token,
                userId: sanitizedUser?.id,
                email: sanitizedUser?.email,
                name: sanitizedUser?.name,
                emailTemplates: sanitizedUser?.emailTemplates || { bodyTemplates: [], footer: '' },
                nylasEmails: sanitizedUser?.nylasEmails ? mapNylasEmailsToExtensionFormat(sanitizedUser?.nylasEmails) : [],
                application: sanitizedApplication
                    ? ({
                          projectName: sanitizedApplication.projectName,
                          features: sanitizedApplication.features || [],
                          integrations: sanitizedApplication.integrations || {},
                          settings: sanitizedApplication.settings || {},
                          subscription: sanitizedApplication.subscription, // Include subscription at application level
                      } as ExtensionApplication)
                    : undefined,
            })
            .catch((error) => {
                console.error('Failed to sync login with extension:', error)
            })

        setAuthToken(token)
        localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
        dispatch(setUser(sanitizedUser))
        dispatch(setApplication(sanitizedApplication))

        return response.data
    } catch (error) {
        return rejectWithValue(error)
    }
})

export const updaterLiteSignup = createAsyncThunk('auth/updaterLiteSignup', async (formData: ExtensionSignUpData, { dispatch, rejectWithValue }) => {
    try {
        const response = await axios.post('/auth/sign-up/updater-lite/verify', { ...formData, source: 'admin_web' })
        const { token, user, application } = response.data

        const sanitizedUser = sanitizeUser(user)

        updaterLiteAuthService
            .syncAuthState({
                token,
                userId: sanitizedUser?.id,
                email: sanitizedUser?.email,
                name: sanitizedUser?.name,
                emailTemplates: sanitizedUser?.emailTemplates || { bodyTemplates: [], footer: '' },
            })
            .catch((error) => {
                console.error('Failed to sync login with updater light:', error)
            })

        const sanitizedApplication = sanitizeApplication(application, sanitizedUser)

        setAuthToken(token)
        localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
        dispatch(setUser(sanitizedUser))
        dispatch(setApplication(sanitizedApplication))

        return response.data
    } catch (error) {
        return rejectWithValue(error)
    }
})

export const updaterLiteGoogleSignup = createAsyncThunk('auth/updaterLiteGoogleSignup', async (formData: GoogleProfile, { dispatch, rejectWithValue }) => {
    try {
        const response = await axios.post('/auth/sign-up/updater-lite/google', { ...formData, source: 'admin_web' })
        const { token, user, application } = response.data

        const sanitizedUser = sanitizeUser(user)

        updaterLiteAuthService
            .syncAuthState({
                token,
                userId: sanitizedUser?.id,
                email: sanitizedUser?.email,
                name: sanitizedUser?.name,
                emailTemplates: sanitizedUser?.emailTemplates || { bodyTemplates: [], footer: '' },
                nylasEmails: sanitizedUser?.nylasEmails ? mapNylasEmailsToExtensionFormat(sanitizedUser?.nylasEmails) : [],
            })
            .catch((error) => {
                console.error('Failed to sync login with updater light:', error)
            })

        const sanitizedApplication = sanitizeApplication(application, sanitizedUser)

        setAuthToken(token)
        localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
        dispatch(setUser(sanitizedUser))
        dispatch(setApplication(sanitizedApplication))

        return response.data
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data?.message || 'Failed to process Google login')
        }
        return rejectWithValue('Failed to process Google login')
    }
})

export const updaterLiteGoogleSignIn = createAsyncThunk(
    'auth/updaterLiteGoogleSignIn',
    async (formData: GoogleSignInProfile, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.post('/auth/sign-in/google', formData)
            const { token, user, application } = response.data

            const sanitizedUser = sanitizeUser(user)

            updaterLiteAuthService
                .syncAuthState({
                    token,
                    userId: sanitizedUser?.id,
                    email: sanitizedUser?.email,
                    name: sanitizedUser?.name,
                    emailTemplates: sanitizedUser?.emailTemplates || { bodyTemplates: [], footer: '' },
                })
                .catch((error) => {
                    console.error('Failed to sync login with extension:', error)
                })

            const sanitizedApplication = sanitizeApplication(application, sanitizedUser)

            setAuthToken(token)
            localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
            dispatch(setUser(sanitizedUser))
            dispatch(setApplication(sanitizedApplication))

            return response.data
        } catch (error) {
            if (error instanceof AxiosError) {
                return rejectWithValue(error.response?.data?.message || 'Failed to process Google login')
            }
            return rejectWithValue('Failed to process Google login')
        }
    }
)

export const googleSignup = createAsyncThunk('auth/signupGoogle', async (formData: GoogleProfile, { dispatch, rejectWithValue }) => {
    try {
        const response = await axios.post('/auth/sign-up/google', { ...formData, source: 'admin_web' })
        const { token, user, application } = response.data

        const sanitizedUser = sanitizeUser(user)

        extensionAuthService
            .syncAuthState({
                token,
                userId: sanitizedUser?.id,
                email: sanitizedUser?.email,
                name: sanitizedUser?.name,
                emailTemplates: sanitizedUser?.emailTemplates || { bodyTemplates: [], footer: '' },
                nylasEmails: sanitizedUser?.nylasEmails ? mapNylasEmailsToExtensionFormat(sanitizedUser?.nylasEmails) : [],
            })
            .catch((error) => {
                console.error('Failed to sync login with extension:', error)
            })

        const sanitizedApplication = sanitizeApplication(application, sanitizedUser)

        setAuthToken(token)
        localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
        dispatch(setUser(sanitizedUser))
        dispatch(setApplication(sanitizedApplication))

        return response.data
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data?.message || 'Failed to process Google login')
        }
        return rejectWithValue('Failed to process Google login')
    }
})

export const googleSignIn = createAsyncThunk('auth/googleSignIn', async (formData: GoogleSignInProfile, { dispatch, rejectWithValue }) => {
    try {
        const response = await axios.post('/auth/sign-in/google', formData)
        const { token, user, application } = response.data

        const sanitizedUser = sanitizeUser(user)
        const sanitizedApplication = sanitizeApplication(application, sanitizedUser)

        extensionAuthService
            .syncAuthState({
                token,
                userId: sanitizedUser?.id,
                email: sanitizedUser?.email,
                name: sanitizedUser?.name,
                emailTemplates: sanitizedUser?.emailTemplates || { bodyTemplates: [], footer: '' },
                nylasEmails: sanitizedUser?.nylasEmails ? mapNylasEmailsToExtensionFormat(sanitizedUser?.nylasEmails) : [],
                integrations: sanitizedUser?.integrations || {},
                application: sanitizedApplication
                    ? ({
                          projectName: sanitizedApplication.projectName,
                          features: sanitizedApplication.features || [],
                          integrations: sanitizedApplication.integrations || {},
                          subscription: sanitizedApplication.subscription, // Include subscription at application level
                      } as ExtensionApplication)
                    : undefined,
            })
            .catch((error) => {
                console.error('Failed to sync login with extension:', error)
            })

        setAuthToken(token)
        localStorage.setItem('loadops_auth_data', JSON.stringify(buildMinimalAuthData(token, response.data)))
        dispatch(setUser(sanitizedUser))
        dispatch(setApplication(sanitizedApplication))

        return response.data
    } catch (error) {
        // Propagate the original error (matches the login thunk above) so the
        // component catch block can inspect response.data.errorCode and route
        // accordingly — e.g. showing the "no account" alert with a sign-up
        // link when the backend signals USER_NOT_FOUND.
        return rejectWithValue(error)
    }
})

export const forgotPassword = createAsyncThunk('auth/forgotPassword', async (formData: ForgotPasswordData, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/auth/forgot-password', formData)
        return data
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send reset code')
        }
        return rejectWithValue('Failed to send reset code')
    }
})

export const verifyResetCode = createAsyncThunk('auth/verifyResetCode', async (params: { email: string; code: string }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/auth/verify-code', params)
        return data
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data?.message || 'Failed to verify code')
        }
        return rejectWithValue('Failed to verify code')
    }
})

export const resetPassword = createAsyncThunk('auth/resetPassword', async (params: { password: string; email: string; code: string }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/auth/reset-password', params)
        return data
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data?.message || 'Failed to reset password')
        }
        return rejectWithValue('Failed to reset password')
    }
})

export const loadDebugData = createAsyncThunk('auth/loadDebugData', async (_, { dispatch }) => {
    const savedPhone = localStorage.getItem(LS_PHONE_KEY)
    const savedEmail = localStorage.getItem(LS_EMAIL_KEY)

    dispatch(
        updateDebugData({
            spotFinder: {
                mockBrokerPhoneNumber: savedPhone || '',
                mockBrokerEmail: savedEmail || '',
            },
        })
    )
})

// Selectors
export const selectUser = (state: RootState) => state.auth.user
export const selectApplication = (state: RootState) => state.auth.application
export const selectOrganizationData = (state: RootState) => state.auth.organizationData
export const selectFeatureFlags = (state: RootState) => state.auth.featureFlags
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated
export const selectIsLoading = (state: RootState) => state.auth.isLoading
export const selectDebugData = (state: RootState) => state.auth.debugData
export const selectVerifyFailed = (state: RootState) => state.auth.verifyFailed

// Selector for checking if the current user is an admin
export const selectIsUserAdmin = (state: RootState) => {
    const { user, application } = state.auth
    return application?.users?.some((appUser) => appUser.email === user?.email && appUser.role === UserRole.ADMIN) ?? false
}

export const mapNylasEmailsToExtensionFormat = (nylasEmails: UserNylasEmails[]) => {
    return (
        nylasEmails?.map((email) => ({
            id: email.email,
            email: email.email,
            isPrimary: email.isPrimary,
            status: email.isPrimary ? 'verified' : 'pending',
            type: email.email.includes('gmail') ? 'gmail' : email.email.includes('outlook') ? 'outlook' : 'other',
        })) || []
    )
}

// Type-safe reducer export
const authReducer = authSlice.reducer
export default authReducer
